import { useCallback, useRef } from "react";

const isMuted = () => localStorage.getItem("kids_sound_muted") === "1";

export default function useGameSounds() {
  const ctx = useRef(null);

  const getCtx = () => {
    if (!ctx.current) {
      ctx.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Browsers (esp. iOS Safari / mobile Chrome) start the context "suspended"
    // until a user gesture. Calling resume() inside a tap handler unlocks audio.
    if (ctx.current.state === "suspended") {
      ctx.current.resume().catch(() => {});
    }
    return ctx.current;
  };

  const tone = useCallback((freq, startTime, duration, vol = 0.3, type = "sine") => {
    const ac  = getCtx();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);
    gain.gain.setValueAtTime(vol, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    osc.start(startTime);
    osc.stop(startTime + duration);
  }, []);

  const playCorrect = useCallback(() => {
    if (isMuted()) return;
    const ac = getCtx();
    const t  = ac.currentTime;
    tone(523, t,        0.12, 0.3); // C5
    tone(659, t + 0.12, 0.12, 0.3); // E5
    tone(784, t + 0.24, 0.22, 0.3); // G5
  }, [tone]);

  const playWrong = useCallback(() => {
    if (isMuted()) return;
    const ac = getCtx();
    const t  = ac.currentTime;
    tone(300, t,       0.15, 0.25, "square");
    tone(220, t + 0.15, 0.2, 0.2, "square");
  }, [tone]);

  const playComplete = useCallback((perfect = false) => {
    if (isMuted()) return;
    const ac = getCtx();
    const t  = ac.currentTime;
    if (perfect) {
      // Fanfare
      [523, 659, 784, 1047].forEach((f, i) => tone(f, t + i * 0.13, 0.18, 0.28));
      tone(1047, t + 0.6, 0.5, 0.3);
    } else {
      [523, 659, 784].forEach((f, i) => tone(f, t + i * 0.13, 0.15, 0.25));
    }
  }, [tone]);

  const playClick = useCallback(() => {
    if (isMuted()) return;
    const ac = getCtx();
    tone(800, ac.currentTime, 0.05, 0.15, "triangle");
  }, [tone]);

  const playTick = useCallback(() => {
    if (isMuted()) return;
    const ac = getCtx();
    tone(440, ac.currentTime, 0.04, 0.08, "triangle");
  }, [tone]);

  return { playCorrect, playWrong, playComplete, playClick, playTick };
}
